import React from 'react';
import { Badge } from '@/components/ui/badge';
import { ScopeProposalBody } from '@/components/estimates/ScopeProposal';
import { ProposalAcceptance } from '@/components/estimates/ProposalAcceptance';
import { useResolvedImageUrl } from '@/components/estimates/ProposalImage';
import { TMAgreementBody } from '@/components/estimates/TMAgreement';
import { EditableText } from '@/components/estimates/EditableText';
import { getLegalPack, resolveLegalState } from '@/lib/legalPacks';
import { resolveBranding } from '@/lib/branding';
import {
  formatUsd,
  computeSectionTotals,
  sectionItemTotal,
} from '@/lib/estimatesData';
import {
  Wallet,
  Package,
  Lock,
  ClipboardList,
  MapPin,
  Calendar as CalendarIcon,
  AlertTriangle,
  Phone,
  Mail,
  Globe,
  User,
  Building2,
  FileSignature,
  ShieldCheck,
  Receipt,
  HandCoins,
} from 'lucide-react';

// Phone-only "1 ls × $9,500" under a line's description. The Qty, Unit and
// Rate columns are hidden below sm so Description + Total fit a phone (the
// client portal's proposal tab renders this same component).
const QtyRateLine = ({ qty, unit, rate }) => (
  <div className="sm:hidden mt-0.5 text-[11px] text-muted-foreground tabular-nums">
    {Number(qty) || 0} {unit || 'ea'} × {formatUsd(rate)}
  </div>
);

const ALLOWANCE_HELP = `Allowances are placeholder budgets for items still being decided (e.g., tile, fixtures, lighting). They're listed here for transparency but are tracked separately from the contract subtotal.`;

const formatPreviewDate = (iso) => {
  if (!iso || typeof iso !== 'string') return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return iso;
  const [, y, mo, d] = m;
  const dt = new Date(Number(y), Number(mo) - 1, Number(d));
  if (Number.isNaN(dt.getTime())) return iso;
  try {
    return dt.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch (e) {
    return iso;
  }
};

/**
 * Per-section block — handles bottom-line mode by hiding rates/totals on rows.
 */
const NO_EDIT = { editable: false };

export const PreviewSection = ({ section, edit = NO_EDIT }) => {
  const t = computeSectionTotals(section);
  const isBottomLine = !!section.bottom_line_mode;
  const items = section.items || [];
  const materials = section.materials || [];
  const allowances = section.allowances || [];

  return (
    <div
      className="rounded-md border border-border overflow-hidden"
      data-testid={`preview-section-${section.id}`}
    >
      {/* Section header — name + optional "Lump sum" badge. Per the
       * "scope first, price last" UX principle, the section subtotal is
       * shown ONLY in the prominent footer banner below, never in the
       * header (which would push a number above the line items). */}
      <div className="flex items-center justify-between border-b border-border px-4 py-2 bg-[hsl(var(--muted))]/30">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-foreground">
            {edit.editable ? (
              <EditableText
                editable
                multiline={false}
                value={section.name || ''}
                placeholder="Section name…"
                onCommit={(name) => edit.onUpdateSection?.(section.id, { name })}
              />
            ) : (
              section.name || 'Section'
            )}
          </span>
          {isBottomLine ? (
            <Badge
              variant="outline"
              className="bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] border-[hsl(var(--primary))]/30 uppercase text-[9px] tracking-[0.16em] inline-flex items-center gap-1"
            >
              <Lock size={9} />
              Lump sum
            </Badge>
          ) : null}
        </div>
      </div>

      {/* Main scope items */}
      {items.length > 0 ? (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              <th className="text-left px-4 py-2">Description</th>
              {!isBottomLine ? (
                <>
                  <th className="text-right px-2 py-2 hidden sm:table-cell">Qty</th>
                  <th className="text-right px-2 py-2 hidden sm:table-cell">Unit</th>
                  <th className="text-right px-2 py-2 hidden sm:table-cell">Rate</th>
                  <th className="text-right px-4 py-2">Total</th>
                </>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr
                key={it.id}
                className="border-t border-border"
                data-testid={`preview-line-${it.id}`}
              >
                <td className="text-left px-4 py-2 text-foreground">
                  <div className="flex items-start gap-2 flex-wrap">
                    {edit.editable ? (
                      <EditableText
                        editable
                        multiline={false}
                        value={it.desc || ''}
                        placeholder="Description…"
                        onCommit={(desc) =>
                          edit.onUpdateItem?.(section.id, it.id, { desc })
                        }
                        className="break-words flex-1 min-w-0"
                      />
                    ) : (
                      <span className="break-words">{it.desc || '—'}</span>
                    )}
                    {it.ho_pick ? (
                      <Badge
                        variant="outline"
                        className="bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-400/30 uppercase text-[9px] tracking-[0.16em] shrink-0"
                      >
                        H/O-P
                      </Badge>
                    ) : null}
                  </div>
                  {!isBottomLine ? (
                    <QtyRateLine qty={it.qty} unit={it.unit} rate={it.rate} />
                  ) : null}
                </td>
                {!isBottomLine ? (
                  <>
                    <td className="text-right px-2 py-2 text-foreground tabular-nums hidden sm:table-cell">
                      {Number(it.qty) || 0}
                    </td>
                    <td className="text-right px-2 py-2 text-muted-foreground hidden sm:table-cell">
                      {it.unit || 'ea'}
                    </td>
                    <td className="text-right px-2 py-2 text-foreground tabular-nums hidden sm:table-cell">
                      {formatUsd(it.rate)}
                    </td>
                    <td className="text-right px-4 py-2 font-semibold text-foreground tabular-nums">
                      {formatUsd(sectionItemTotal(it))}
                    </td>
                  </>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}

      {/* Materials sub-block */}
      {materials.length > 0 ? (
        <div
          className="border-t border-border bg-foreground/[0.02]"
          data-testid={`preview-materials-${section.id}`}
        >
          <div className="px-4 py-2 flex items-center justify-between">
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">
              <Package size={12} />
              Materials
            </div>
            {!isBottomLine ? (
              <div className="text-sm font-semibold text-foreground tabular-nums">
                {formatUsd(t.section_materials_total)}
              </div>
            ) : null}
          </div>
          <table className="w-full text-sm">
            <tbody>
              {materials.map((row) => (
                <tr
                  key={row.id}
                  className="border-t border-border/60"
                  data-testid={`preview-mat-${row.id}`}
                >
                  <td className="text-left px-4 py-1.5 text-foreground">
                    {row.desc || '—'}
                    {!isBottomLine ? (
                      <QtyRateLine qty={row.qty} unit={row.unit} rate={row.rate} />
                    ) : null}
                  </td>
                  {!isBottomLine ? (
                    <>
                      <td className="text-right px-2 py-1.5 text-foreground tabular-nums w-16 hidden sm:table-cell">
                        {Number(row.qty) || 0}
                      </td>
                      <td className="text-right px-2 py-1.5 text-muted-foreground w-14 hidden sm:table-cell">
                        {row.unit || 'ea'}
                      </td>
                      <td className="text-right px-2 py-1.5 text-foreground tabular-nums w-24 hidden sm:table-cell">
                        {formatUsd(row.rate)}
                      </td>
                      <td className="text-right px-4 py-1.5 font-semibold text-foreground tabular-nums w-28">
                        {formatUsd(sectionItemTotal(row))}
                      </td>
                    </>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
          {section.materials_notes && section.materials_notes.trim() ? (
            <div
              className="border-t border-border/60 px-4 py-2 text-[12px] text-foreground/80 whitespace-pre-wrap"
              data-testid={`preview-materials-notes-${section.id}`}
            >
              {section.materials_notes}
            </div>
          ) : null}
        </div>
      ) : null}

      {/* Allowances sub-block (informational) */}
      {allowances.length > 0 ? (
        <div
          className="border-t border-amber-400/30 bg-amber-500/5"
          data-testid={`preview-allowances-${section.id}`}
        >
          <div className="px-4 py-2 flex items-center justify-between">
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-amber-800/90 dark:text-amber-200/90 font-semibold">
              <Wallet size={12} />
              Allowances
              <Badge
                variant="outline"
                className="bg-amber-500/10 text-amber-800 dark:text-amber-200 border-amber-400/30 uppercase text-[9px] tracking-[0.16em]"
              >
                Informational
              </Badge>
            </div>
            <div className="text-sm font-semibold text-amber-800 dark:text-amber-200 tabular-nums">
              {formatUsd(t.section_allowances_total)}
            </div>
          </div>
          <table className="w-full text-sm">
            <tbody>
              {allowances.map((row) => (
                <tr
                  key={row.id}
                  className="border-t border-amber-400/20"
                  data-testid={`preview-alw-${row.id}`}
                >
                  <td className="text-left px-4 py-1.5 text-foreground">
                    {row.desc || '—'}
                    <QtyRateLine qty={row.qty} unit={row.unit} rate={row.rate} />
                  </td>
                  <td className="text-right px-2 py-1.5 text-foreground tabular-nums w-16 hidden sm:table-cell">
                    {Number(row.qty) || 0}
                  </td>
                  <td className="text-right px-2 py-1.5 text-muted-foreground w-14 hidden sm:table-cell">
                    {row.unit || 'ea'}
                  </td>
                  <td className="text-right px-2 py-1.5 text-foreground tabular-nums w-24 hidden sm:table-cell">
                    {formatUsd(row.rate)}
                  </td>
                  <td className="text-right px-4 py-1.5 font-semibold text-amber-800 dark:text-amber-200 tabular-nums w-28">
                    {formatUsd(sectionItemTotal(row))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {section.allowances_notes && section.allowances_notes.trim() ? (
            <div
              className="border-t border-amber-400/30 px-4 py-2 text-[12px] text-amber-800/90 dark:text-amber-100/90 whitespace-pre-wrap"
              data-testid={`preview-allowances-notes-${section.id}`}
            >
              {section.allowances_notes}
            </div>
          ) : null}
        </div>
      ) : null}

      {/* Section notes */}
      {section.notes && section.notes.trim() ? (
        <div
          className="border-t border-border px-4 py-2 text-[12px]"
          data-testid={`preview-section-notes-${section.id}`}
        >
          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-0.5">
            Notes
          </div>
          <div className="whitespace-pre-wrap text-foreground/80">
            {section.notes}
          </div>
        </div>
      ) : null}

      {/* Prominent section total footer */}
      <div
        className="border-t border-[hsl(var(--primary))]/30 bg-[hsl(var(--primary))]/10 px-4 py-3 flex items-center justify-between"
        data-testid={`preview-section-total-banner-${section.id}`}
      >
        <span className="text-[11px] sm:text-xs uppercase tracking-[0.22em] text-[hsl(var(--primary))]/90 font-semibold inline-flex items-center gap-2">
          {isBottomLine ? <Lock size={11} /> : null}
          Section total
        </span>
        <span
          className="text-xl sm:text-2xl font-bold text-[hsl(var(--primary))] tabular-nums leading-none"
          data-testid={`preview-section-total-prominent-${section.id}`}
        >
          {formatUsd(t.section_display_total)}
        </span>
      </div>
    </div>
  );
};

/* ----------------------------------------------------------------------------
 * Helpers — shared by the new client-facing layout
 * ------------------------------------------------------------------------- */

// Heuristic split for an unstructured single-line client address.
// Returns {street, locality}. If we can't split cleanly, street holds the
// full string and locality is null. Used until a structured address is
// added to the clients schema (NET-NEW — pending sign-off).
const splitClientAddress = (addr) => {
  if (!addr || typeof addr !== 'string') return { street: null, locality: null };
  const trimmed = addr.trim();
  if (!trimmed) return { street: null, locality: null };
  const firstComma = trimmed.indexOf(',');
  if (firstComma === -1) return { street: trimmed, locality: null };
  const street = trimmed.slice(0, firstComma).trim();
  const locality = trimmed.slice(firstComma + 1).trim();
  return { street, locality: locality || null };
};

// Right-aligned contact row used in the header block.
const HeaderContactLine = ({ icon: Icon, children, testId }) =>
  children ? (
    <div
      className="flex items-center justify-end gap-1.5 text-xs text-muted-foreground"
      data-testid={testId}
    >
      {Icon ? <Icon size={11} className="text-[hsl(var(--primary))]/70" /> : null}
      <span className="text-foreground/90">{children}</span>
    </div>
  ) : null;

// One labeled term block. Divider auto-applied except for the first block
// (parent uses `divide-y`). Pass `emphasize` to highlight the row (used for
// Total Investment).
const TermBlock = ({ title, icon: Icon, emphasize, testId, children }) => (
  <div
    className={`px-4 py-4 ${emphasize ? 'bg-[hsl(var(--primary))]/[0.04]' : ''}`}
    data-testid={testId}
  >
    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-2">
      {Icon ? <Icon size={11} className="text-[hsl(var(--primary))]" /> : null}
      <span className={emphasize ? 'text-[hsl(var(--primary))] font-semibold' : ''}>
        {title}
      </span>
    </div>
    <div className="text-sm text-foreground leading-relaxed">{children}</div>
  </div>
);

// Renders the user-entered value when present, otherwise renders the
// "Not yet captured" placeholder. Used by the 7 Phase 26 fields that were
// stubs in the audit pass and are now live columns from migration 012.
const ProposalTermText = ({ value, placeholderLabel }) =>
  value && String(value).trim() ? (
    <span className="whitespace-pre-wrap">{String(value)}</span>
  ) : (
    <>
      {/* On screen: nudge the contractor to fill the field. In print: a clean
          neutral line — the nudge must never reach a client's copy. */}
      <PendingFieldPlaceholder label={placeholderLabel} />
      <span className="hidden print:inline italic text-muted-foreground/70">
        None noted.
      </span>
    </>
  );

// Placeholder rendered inline for net-new fields that aren't captured in
// the data model yet. Net-new fields render this until you sign off on
// the schema migration.
const PendingFieldPlaceholder = ({ label }) => (
  // `print:hidden`: this is an internal reminder for the contractor — it must
  // never appear on the client-facing PRINTED proposal ("to be captured once
  // the field is added…" showed up on a real bid). On-screen it stays as the
  // schema-decision nudge it was built to be.
  <div
    className="print:hidden text-xs italic text-muted-foreground/70 border border-dashed border-border rounded px-3 py-2 bg-[hsl(var(--muted))]/30"
    data-testid={`preview-pending-${(label || '').toLowerCase().replace(/\s+/g, '-')}`}
  >
    {label} — not filled in yet. Add it in the estimate editor.
  </div>
);

/**
 * Print-friendly client-ready proposal view.
 *
 * Layout (top → bottom):
 *   1. Header — logo + brand + slogan + "Proposal" eyebrow + Prepared by
 *      [placeholder]; right-aligned Phone / Email / Website
 *   2. Proposal meta — Date issued + Proposal Valid For [placeholder]
 *   3. "Proposal for" — Client name + Address (street / city-state-zip) +
 *      Phone + Email
 *   4. Legend — (H/O-P) = Homeowner Purchase
 *   5. Project terms (single card, internal dividers):
 *      Estimated Project Start, Estimated Duration, Project Description,
 *      Scope of Work, Allowances, Exclusions [placeholder],
 *      Special Clauses, Client Responsibilities [placeholder],
 *      Company Responsibilities [placeholder], Change-Order Clause [placeholder],
 *      Warranty [placeholder], Payment Schedule, Total Investment
 *
 * Props:
 *   estimate — hydrated estimate row (sections, overview, payment_schedule, etc.)
 *   totals   — computed totals (from computeEstimateTotals)
 *   company  — company_profile settings (name, logo_url, phone, email, website, tagline)
 *   client   — full client row (name, address, phone, email) — optional;
 *              EstimatesPage.js looks it up by client_id and passes it in.
 */
export const EstimatePreview = ({ estimate, totals, company, client, edit = NO_EDIT }) => {
  const sections = Array.isArray(estimate?.sections) ? estimate.sections : [];
  const schedule = totals?.payment_schedule || [];
  const brandName = company?.name || 'K.O. CO+RE SYSTEMS';
  const slogan = company?.tagline || 'Construction & Remodeling Services';
  // Logo lives in the PRIVATE portal-photos bucket — sign it, don't use the
  // stored public URL (which 404s). Same fix as proposal photos.
  const logoSrc = useResolvedImageUrl({ url: company?.logo_url, path: company?.logo_path });

  const hasAllowances = (totals?.allowances_total || 0) > 0;
  const hasHOP = (totals?.hop_total || 0) > 0;

  const overview = estimate?.overview || {};
  const isScope = estimate?.layout === 'scope';
  const isTM = estimate?.layout === 'tm';

  // White-label branding (accent color / header font / logo placement).
  const branding = resolveBranding(company?.branding);
  const headerClass =
    branding.logoPlacement === 'center'
      ? 'flex flex-col items-center gap-4 text-center border-b border-border pb-5'
      : branding.logoPlacement === 'right'
      ? 'flex items-start gap-4 flex-row-reverse border-b border-border pb-5'
      : 'flex items-start gap-4 border-b border-border pb-5';

  const issuedDate = estimate?.created_at
    ? formatPreviewDate(String(estimate.created_at).slice(0, 10))
    : '';

  // Client info (prefer the passed-in client row over the denormalized
  // client_name on the estimate). Address is split heuristically.
  const clientName =
    client?.name || estimate?.client_name || 'Client TBD';
  const clientAddressRaw = client?.address || '';
  const { street: clientStreet, locality: clientLocality } =
    splitClientAddress(clientAddressRaw);
  const clientPhone = client?.phone || '';
  const clientEmail = client?.email || '';

  return (
    <div
      className="rounded-md border border-[hsl(var(--hairline))] bg-[hsl(var(--card))] p-6 sm:p-10 print-area"
      style={branding.containerStyle}
      data-testid="estimate-preview"
    >
      {edit.editable ? (
        <div
          className="print:hidden mb-5 rounded-md border border-[hsl(var(--primary))]/30 bg-[hsl(var(--primary))]/[0.06] px-3 py-2 text-xs text-[hsl(var(--primary))]"
          data-testid="preview-edit-hint"
        >
          ✏️ Click any text to edit it right here — changes save automatically.
        </div>
      ) : null}
      {/* ============================================================
       * 1) HEADER BLOCK — single left-aligned stacked column
       * ============================================================ */}
      <header className={headerClass} data-testid="preview-header">
        {company?.logo_url ? (
          <img
            src={logoSrc}
            alt={`${brandName} logo`}
            className="h-14 sm:h-16 w-auto max-w-[160px] object-contain rounded-sm border border-[hsl(var(--hairline))] bg-[#0f0e0c] p-1 shrink-0"
            data-testid="estimate-preview-logo"
          />
        ) : null}
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-[0.32em] text-muted-foreground">
            Proposal
          </div>
          <h1
            className={`text-xl sm:text-3xl md:text-4xl max-sm:!tracking-[0.06em] text-[hsl(var(--primary))] leading-tight mt-1 break-words ${
              branding.useBrandClass ? 'font-brand' : 'font-semibold'
            }`}
            style={
              branding.brandFontFamily
                ? { fontFamily: branding.brandFontFamily }
                : undefined
            }
            data-testid="preview-brand-name"
          >
            {brandName}
          </h1>
          <div
            className="mt-2 text-xs text-muted-foreground"
            data-testid="preview-brand-slogan"
          >
            {slogan}
          </div>
          {/* Whole row hides in print when empty — "Prepared by — not
              specified" on a client's copy reads as sloppiness. */}
          <div
            className={`mt-2 text-[11px] uppercase tracking-[0.18em] text-muted-foreground/80 ${
              estimate?.prepared_by && estimate.prepared_by.trim() ? '' : 'print:hidden'
            }`}
            data-testid="preview-prepared-by"
          >
            Prepared by
            {estimate?.prepared_by && estimate.prepared_by.trim() ? (
              <span className="ml-2 text-foreground/90 normal-case tracking-normal font-medium">
                {estimate.prepared_by}
              </span>
            ) : (
              <span className="ml-2 italic text-muted-foreground/60 normal-case tracking-normal">
                — not specified
              </span>
            )}
          </div>
          {/* Contact details — moved out of the top-right and stacked
              left-aligned below "Prepared by" so the company name owns
              the top of the header without crowding. */}
          {(company?.phone || company?.email || company?.website) ? (
            <div
              className="mt-3 space-y-1"
              data-testid="preview-header-contact-stack"
            >
              {company?.phone ? (
                <div
                  className="flex items-center gap-1.5 text-xs text-muted-foreground"
                  data-testid="preview-header-phone"
                >
                  <Phone size={11} className="text-[hsl(var(--primary))]/70" />
                  <span className="text-foreground/90">{company.phone}</span>
                </div>
              ) : null}
              {company?.email ? (
                <div
                  className="flex items-center gap-1.5 text-xs text-muted-foreground"
                  data-testid="preview-header-email"
                >
                  <Mail size={11} className="text-[hsl(var(--primary))]/70" />
                  <span className="text-foreground/90">{company.email}</span>
                </div>
              ) : null}
              {company?.website ? (
                <div
                  className="flex items-center gap-1.5 text-xs text-muted-foreground"
                  data-testid="preview-header-website"
                >
                  <Globe size={11} className="text-[hsl(var(--primary))]/70" />
                  <span className="text-foreground/90">{company.website}</span>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </header>

      {/* ============================================================
       * 2) PROPOSAL META — Date + Valid For
       * ============================================================ */}
      <section
        className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-5"
        data-testid="preview-meta"
      >
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
            Date
          </div>
          <div
            className="mt-1 text-sm font-semibold text-foreground"
            data-testid="preview-date"
          >
            {issuedDate || '—'}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
            Proposal valid for
          </div>
          <div className="mt-1" data-testid="preview-valid-for">
            {estimate?.proposal_valid_for && estimate.proposal_valid_for.trim() ? (
              <div className="text-sm font-semibold text-foreground">
                {estimate.proposal_valid_for}
              </div>
            ) : (
              <PendingFieldPlaceholder label="Proposal valid-for window" />
            )}
          </div>
        </div>
      </section>

      {/* ============================================================
       * 3) CLIENT INFORMATION — "Proposal for"
       * ============================================================ */}
      <section
        className="mt-6 rounded-md border border-border overflow-hidden"
        data-testid="preview-client-info"
      >
        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border bg-[hsl(var(--muted))]/40">
          <User size={14} className="text-[hsl(var(--primary))]" />
          <h2 className="text-[11px] sm:text-xs uppercase tracking-[0.22em] font-semibold text-foreground">
            Proposal for
          </h2>
        </div>
        <div className="px-4 py-4 space-y-2.5">
          <div
            className="text-base font-semibold text-foreground"
            data-testid="preview-client-name"
          >
            {clientName}
          </div>
          {clientStreet ? (
            <div className="text-sm text-foreground/90 leading-snug">
              <div data-testid="preview-client-address-street">{clientStreet}</div>
              {clientLocality ? (
                <div data-testid="preview-client-address-locality">
                  {clientLocality}
                </div>
              ) : null}
            </div>
          ) : null}
          {clientPhone ? (
            <div className="flex items-center gap-2 text-sm text-foreground/90">
              <Phone size={12} className="text-muted-foreground" />
              <span data-testid="preview-client-phone">{clientPhone}</span>
            </div>
          ) : null}
          {clientEmail ? (
            <div className="flex items-center gap-2 text-sm text-foreground/90">
              <Mail size={12} className="text-muted-foreground" />
              <span data-testid="preview-client-email">{clientEmail}</span>
            </div>
          ) : null}
          {!clientStreet && !clientPhone && !clientEmail ? (
            <div className="text-xs italic text-muted-foreground/70">
              Client contact details not yet on file.
            </div>
          ) : null}
        </div>
      </section>

      {/* ============================================================
       * 4) LEGEND (proposal layouts only)
       * ============================================================ */}
      {!isTM ? (
        <p
          className="mt-4 text-[11px] text-muted-foreground italic"
          data-testid="preview-legend"
        >
          <Badge
            variant="outline"
            className="uppercase text-[9px] tracking-[0.16em] bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-400/30 px-1.5 py-0 mr-1.5 not-italic"
          >
            H/O-P
          </Badge>
          = Homeowner Purchase
        </p>
      ) : null}

      {/* Time & Material agreement body (layout === 'tm') */}
      {isTM ? <TMAgreementBody estimate={estimate} /> : null}

      {/* ============================================================
       * 5) PROJECT TERMS — single card, internal dividers
       *    (proposal layouts only; T&M uses its own body above)
       * ============================================================ */}
      {!isTM ? (
      <section
        className="mt-5 rounded-md border border-border overflow-hidden divide-y divide-border"
        data-testid="preview-terms"
      >
        {/* Estimated Project Start + Completion intentionally REMOVED from the
            client-facing proposal (Phase 26 step 2 refinement). Internal
            scheduling still uses overview.est_start / overview.est_end in
            EstimateBuilder.js — only the client-visible render drops them. */}

        {/* Estimated Duration (text field — still surfaced on the proposal) */}
        <TermBlock
          title="Estimated Duration"
          icon={CalendarIcon}
          testId="preview-term-est-duration"
        >
          {edit.editable ? (
            <EditableText
              editable
              multiline={false}
              value={estimate?.estimated_duration || ''}
              placeholder="Estimated duration (e.g. 3–4 weeks)…"
              onCommit={(estimated_duration) =>
                edit.onPatchEstimate?.({ estimated_duration })
              }
            />
          ) : estimate?.estimated_duration && estimate.estimated_duration.trim()
            ? estimate.estimated_duration
            : 'TBD'}
        </TermBlock>

        {/* Project Description */}
        <TermBlock
          title="Project Description"
          icon={ClipboardList}
          testId="preview-term-description"
        >
          {edit.editable ? (
            <EditableText
              editable
              value={overview.description || ''}
              placeholder="Project description…"
              onCommit={(description) => edit.onPatchOverview?.({ description })}
              as="span"
              className="block whitespace-pre-wrap"
            />
          ) : overview.description && overview.description.trim() ? (
            <span className="whitespace-pre-wrap">{overview.description}</span>
          ) : (
            <span className="italic text-muted-foreground/70">
              No description provided.
            </span>
          )}
        </TermBlock>

        {/* Scope of Work */}
        <TermBlock
          title="Scope of Work"
          icon={Package}
          testId="preview-term-scope"
        >
          {edit.editable ? (
            <div className="whitespace-pre-wrap mb-4">
              <EditableText
                editable
                value={overview.scope_summary || ''}
                placeholder="Scope summary…"
                onCommit={(scope_summary) =>
                  edit.onPatchOverview?.({ scope_summary })
                }
                as="span"
                className="block whitespace-pre-wrap"
              />
            </div>
          ) : overview.scope_summary && overview.scope_summary.trim() ? (
            <div className="whitespace-pre-wrap mb-4">
              {overview.scope_summary}
            </div>
          ) : null}
          {sections.length > 0 ? (
            isScope ? (
              <div className="mt-2">
                <ScopeProposalBody estimate={estimate} totals={totals} edit={edit} />
              </div>
            ) : (
              <div className="space-y-5 mt-2">
                {sections.map((section) => (
                  <PreviewSection key={section.id} section={section} edit={edit} />
                ))}
              </div>
            )
          ) : !overview.scope_summary ? (
            <span className="italic text-muted-foreground/70">
              Scope details not yet specified.
            </span>
          ) : null}
        </TermBlock>

        {/* Allowances */}
        <TermBlock
          title="Allowances"
          icon={Wallet}
          testId="preview-term-allowances"
        >
          {hasAllowances ? (
            <div>
              <div className="flex items-center justify-between">
                <span className="text-amber-800/90 dark:text-amber-200/90 inline-flex items-center gap-2 text-sm">
                  Total allowances (informational)
                </span>
                <span
                  className="text-amber-800 dark:text-amber-200 font-semibold tabular-nums text-sm"
                  data-testid="preview-allowances-total"
                >
                  {formatUsd(totals?.allowances_total)}
                </span>
              </div>
              <div className="mt-2 text-[12px] text-amber-800/85 dark:text-amber-100/85 leading-relaxed">
                {ALLOWANCE_HELP}
              </div>
            </div>
          ) : (
            <span className="italic text-muted-foreground/70">
              No allowances included.
            </span>
          )}
        </TermBlock>

        {/* Exclusions — Phase 26 column */}
        <TermBlock
          title="Exclusions"
          icon={AlertTriangle}
          testId="preview-term-exclusions"
        >
          {edit.editable ? (
            <EditableText
              editable
              value={estimate?.exclusions || ''}
              placeholder="Exclusions…"
              onCommit={(v) => edit.onPatchEstimate?.({ exclusions: v })}
              as="span"
              className="block whitespace-pre-wrap"
            />
          ) : (
          <ProposalTermText
              value={estimate?.exclusions}
              placeholderLabel="Exclusions list"
            />
          )}
        </TermBlock>

        {/* Special Clauses — Phase 26 column; falls back to legacy
            overview.special_conditions for old estimates created before 012 */}
        <TermBlock
          title="Special Clauses"
          icon={AlertTriangle}
          testId="preview-term-special-clauses"
        >
          {edit.editable ? (
            <EditableText
              editable
              value={
                estimate?.special_clauses || overview.special_conditions || ''
              }
              placeholder="Special clauses…"
              onCommit={(special_clauses) =>
                edit.onPatchEstimate?.({ special_clauses })
              }
              as="span"
              className="block whitespace-pre-wrap"
            />
          ) : estimate?.special_clauses && estimate.special_clauses.trim() ? (
            <span className="whitespace-pre-wrap">{estimate.special_clauses}</span>
          ) : overview.special_conditions &&
            overview.special_conditions.trim() ? (
            <span className="whitespace-pre-wrap">
              {overview.special_conditions}
            </span>
          ) : (
            <span className="italic text-muted-foreground/70">
              No special clauses noted.
            </span>
          )}
        </TermBlock>

        {/* Client Responsibilities — Phase 26 column */}
        <TermBlock
          title="Client Responsibilities"
          icon={User}
          testId="preview-term-client-responsibilities"
        >
          {edit.editable ? (
            <EditableText
              editable
              value={estimate?.client_responsibilities || ''}
              placeholder="Client responsibilities…"
              onCommit={(v) => edit.onPatchEstimate?.({ client_responsibilities: v })}
              as="span"
              className="block whitespace-pre-wrap"
            />
          ) : (
          <ProposalTermText
              value={estimate?.client_responsibilities}
              placeholderLabel="Client responsibilities"
            />
          )}
        </TermBlock>

        {/* Company Responsibilities — Phase 26 column */}
        <TermBlock
          title="Company Responsibilities"
          icon={Building2}
          testId="preview-term-company-responsibilities"
        >
          {edit.editable ? (
            <EditableText
              editable
              value={estimate?.company_responsibilities || ''}
              placeholder="Company responsibilities…"
              onCommit={(v) => edit.onPatchEstimate?.({ company_responsibilities: v })}
              as="span"
              className="block whitespace-pre-wrap"
            />
          ) : (
          <ProposalTermText
              value={estimate?.company_responsibilities}
              placeholderLabel="Company responsibilities"
            />
          )}
        </TermBlock>

        {/* Change-Order Clause — Phase 26 column (pre-filled from company
            default on new estimates) */}
        <TermBlock
          title="Change-Order Clause"
          icon={FileSignature}
          testId="preview-term-change-order"
        >
          {edit.editable ? (
            <EditableText
              editable
              value={estimate?.change_order_clause || ''}
              placeholder="Change-order clause…"
              onCommit={(v) => edit.onPatchEstimate?.({ change_order_clause: v })}
              as="span"
              className="block whitespace-pre-wrap"
            />
          ) : (
          <ProposalTermText
              value={estimate?.change_order_clause}
              placeholderLabel="Change-order clause"
            />
          )}
        </TermBlock>

        {/* Warranty — Phase 26 column (pre-filled from company default
            on new estimates) */}
        <TermBlock
          title="Warranty"
          icon={ShieldCheck}
          testId="preview-term-warranty"
        >
          {edit.editable ? (
            <EditableText
              editable
              value={estimate?.warranty || ''}
              placeholder="Warranty terms…"
              onCommit={(v) => edit.onPatchEstimate?.({ warranty: v })}
              as="span"
              className="block whitespace-pre-wrap"
            />
          ) : (
          <ProposalTermText
              value={estimate?.warranty}
              placeholderLabel="Warranty terms"
            />
          )}
        </TermBlock>

        {/* Payment Schedule */}
        <TermBlock
          title="Payment Schedule"
          icon={Receipt}
          testId="preview-term-payment-schedule"
        >
          {schedule.length > 0 ? (
            <ul className="divide-y divide-border/60 -mx-1">
              {schedule.map((row) => (
                <li
                  key={row.id || row.label}
                  className="flex items-center justify-between px-1 py-2"
                  data-testid={`preview-schedule-${row.id || row.label}`}
                >
                  <div className="min-w-0 pr-3">
                    <div className="text-sm font-semibold text-foreground">
                      {row.label}{' '}
                      <span className="text-xs text-muted-foreground">
                        ({row.percent}%)
                      </span>
                    </div>
                    <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                      {/^\s*due\b/i.test(row.due || '') ? row.due : `Due ${row.due || 'TBD'}`}
                    </div>
                  </div>
                  <div className="text-sm font-semibold text-foreground tabular-nums shrink-0">
                    {formatUsd(row.amount)}
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <span className="italic text-muted-foreground/70">
              No payment schedule defined.
            </span>
          )}
        </TermBlock>

        {/* Total Investment — emphasized last block */}
        <TermBlock
          title="Total Investment"
          icon={HandCoins}
          emphasize
          testId="preview-term-total-investment"
        >
          <div className="space-y-1.5">
            {hasHOP ? (
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground inline-flex items-center gap-2">
                  <Badge
                    variant="outline"
                    className="uppercase text-[9px] tracking-[0.16em] bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-400/30 px-1.5 py-0"
                  >
                    H/O-P
                  </Badge>
                  Homeowner picks
                </span>
                <span
                  className="text-foreground font-semibold tabular-nums"
                  data-testid="preview-hop-total"
                >
                  {formatUsd(totals?.hop_total)}
                </span>
              </div>
            ) : null}
            {hasAllowances ? (
              <div className="flex items-center justify-between text-sm">
                <span className="text-amber-800/90 dark:text-amber-200/90 inline-flex items-center gap-2">
                  <Wallet size={12} />
                  Allowances (informational)
                </span>
                <span
                  className="text-amber-800 dark:text-amber-200 font-semibold tabular-nums"
                  data-testid="preview-allowances-total-summary"
                >
                  {formatUsd(totals?.allowances_total)}
                </span>
              </div>
            ) : null}
            <div className="flex items-center justify-between text-sm pt-1 border-t border-border/60">
              <span className="text-muted-foreground">Subtotal</span>
              <span
                className="text-foreground font-semibold tabular-nums"
                data-testid="preview-subtotal"
              >
                {formatUsd(totals?.subtotal)}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">
                Tax ({estimate?.tax_rate || 0}%)
              </span>
              <span
                className="text-foreground font-semibold tabular-nums"
                data-testid="preview-tax"
              >
                {formatUsd(totals?.tax)}
              </span>
            </div>
            <div className="flex items-center justify-between border-t border-[hsl(var(--primary))]/30 pt-3 mt-2">
              <span className="text-xs uppercase tracking-[0.22em] text-[hsl(var(--primary))] font-semibold">
                Total
              </span>
              <span
                className="text-2xl font-bold text-[hsl(var(--primary))] tabular-nums"
                data-testid="preview-total"
              >
                {formatUsd(totals?.total)}
              </span>
            </div>
            {hasHOP ? (
              <div className="text-[11px] text-blue-700/70 dark:text-blue-300/70 pt-1 italic">
                Items flagged H/O-P are picked by the homeowner; the contractor
                still installs/coordinates them per scope.
              </div>
            ) : null}
          </div>
        </TermBlock>
      </section>
      ) : null}

      {/* ============================================================
       * 6) CONTRACT PAGES — acceptance / signature (+ optional legal)
       * ============================================================ */}
      {estimate?.include_acceptance ? (
        <ProposalAcceptance
          estimate={estimate}
          totals={totals}
          company={company}
          client={client}
          pack={getLegalPack(resolveLegalState(estimate, company))}
          projectAddress={
            [
              estimate?.project_address_street,
              [
                estimate?.project_address_city,
                [estimate?.project_address_state, estimate?.project_address_zip]
                  .filter(Boolean)
                  .join(' ')
                  .trim(),
              ]
                .filter(Boolean)
                .join(', '),
            ]
              .filter(Boolean)
              .join(', ') ||
            estimate?.overview?.address ||
            ''
          }
        />
      ) : null}

      <div className="mt-6 text-[11px] uppercase tracking-[0.18em] text-muted-foreground text-center print-avoid">
        Thank you for the opportunity. Questions? Reach out any time.
      </div>

      {/* Repeating footer — print / PDF only (hidden on screen via CSS). */}
      <div className="print-running-footer" data-testid="preview-print-footer">
        {[brandName, company?.phone, company?.website || company?.email]
          .filter(Boolean)
          .join('  •  ')}
      </div>
    </div>
  );
};

export default EstimatePreview;
